"use client";

import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { BookOpen, ChevronDown, ChevronRight, CircleHelp, Gift, LogOut, Receipt, UserPlus, Wallet } from "lucide-react";

import { cn } from "@/lib/utils";
import { Avatar } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { RewardsHubBody, useRewardsHubData } from "@/components/promotions/promo-bonus-menu";
import { useAuthModalStore, selectOpenOnboarding } from "@/lib/store/auth-store";
import { MOCK_HOME } from "@/lib/creator-copy/mock";
import { HowArenaV2WorksDialog } from "@/components/arena/v2/how-it-works";
import { DiscordIcon, InstagramIcon, TelegramIcon, XTwitterIcon, YouTubeIcon } from "@/components/ui/icons";
import { usePlanHref } from "@/lib/config/plan-minimum";
import { LINE_HOWTO_EVENT } from "@/components/line/line-howto";
import { SIMPLE_VIEW_EVENT } from "@/lib/simple/events";

/* =============================================================================
   The account menu.

   The avatar is a door to the account, so it opens the account. Same panel
   as the other header menus: 20px radius, white ring, grey-900.

   The top row is the creator: avatar, who they are, and "Creator home"
   under it, which it opens. The rows under it are the account, one glyph
   each, the fact printed on the right where one exists (the wallet's
   balance, rewards waiting). With no sidebar in the v3 shell, this menu is
   where the wallet, entries, rewards, invite and help live, on
   desktop and on a phone.
   ============================================================================= */

function shortWallet(address: string) {
  return `${address.slice(0, 6)}…${address.slice(-4)}`;
}

/* The outbound links production keeps in lib/config/links.ts, which this
   prototype does not have. Same URLs. */
const HELP_CENTER_URL = "https://www.getpicks.app/help-center";
const TERMS_AND_CONDITIONS_URL = "https://www.getpicks.app/terms-and-conditions";

/** What the sidebar's foot carried before v3 removed the sidebar: Game rules
    opens the in-app rules explainer (a row below, not a link), Promotions goes
    to the Help Center, then Terms and Settings. */
const HELP_LINKS = [
  { label: "Promotions", href: HELP_CENTER_URL, external: true },
  { label: "Terms", href: TERMS_AND_CONDITIONS_URL, external: true },
  { label: "Settings", href: "/settings", external: false },
] as const;

/** The community links from the sidebar's foot, same order and URLs. */
const SOCIAL_LINKS = [
  { label: "Discord", href: "https://discord.com/invite/picksdotapp", Icon: DiscordIcon },
  { label: "X (Twitter)", href: "https://x.com/GetPicks_app", Icon: XTwitterIcon },
  { label: "Instagram", href: "https://www.instagram.com/getpicks_app/", Icon: InstagramIcon },
  { label: "YouTube", href: "https://www.youtube.com/@GetPicksApp/shorts", Icon: YouTubeIcon },
  { label: "Telegram", href: "https://t.me/PicksdotappCommunity", Icon: TelegramIcon },
] as const;

/** Money in the menu reads down, never up: $12.349 is $12.34. */
function flooredUsd(balance: string) {
  return `${(Math.floor(parseFloat(balance) * 100) / 100).toFixed(2)}`;
}

export function AccountMenu({
  avatarSrc,
  wallet,
  balance,
  onSignOut,
}: {
  avatarSrc: string;
  /** The account itself: users here are a wallet, not a username. */
  wallet?: string;
  /** USD.P balance as the header already has it; null while loading. */
  balance?: string | null;
  onSignOut: () => void;
}) {
  const planHref = usePlanHref();
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  // The Reward Hub, opened from the menu: the creator shell carries no hub
  // chip, so the hub comes up as a centred dialog over whatever is on screen.
  const [hubOpen, setHubOpen] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const hub = useRewardsHubData();
  const commission = MOCK_HOME.affiliate.commissionThisMonth;
  const openOnboarding = useAuthModalStore(selectOpenOnboarding);
  const ref = useRef<HTMLDivElement>(null);
  const pathname = usePathname();

  // Same mechanics as the other header menus: click outside, Escape, and the
  // panel closes on navigation so it never hangs over the page you asked for.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: MouseEvent) {
      if (ref.current?.contains(event.target as Element)) return;
      setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => setOpen(false), [pathname]);
  // The help group folds back each time the menu closes, so it never comes
  // up already spread.
  useEffect(() => {
    if (!open) setHelpOpen(false);
  }, [open]);

  const row =
    "flex w-full items-center gap-3 px-5 py-3 text-left font-open-sauce text-[15px] font-semibold tracking-tight text-grey-100 transition-colors hover:bg-white-100 hover:text-white";
  const glyph = "size-[18px] shrink-0 text-grey-300";
  const fact = "ml-auto font-open-sauce text-[13px] font-semibold tabular-nums tracking-tight text-grey-300";
  const sub =
    "block w-full py-2 pl-[52px] pr-5 text-left font-open-sauce text-[14px] font-semibold tracking-tight text-grey-300 transition-colors hover:bg-white-100 hover:text-white";

  const rows = [
    { href: "/wallet", view: "wallet", label: "Wallet", Icon: Wallet, fact: balance ? flooredUsd(balance) : null },
    { href: "/entries", view: "entries", label: "My entries", Icon: Receipt, fact: null },
  ];
  // /simple has its own Wallet and My entries: the rows open them in place.
  const inSimple = pathname === "/simple" || pathname === "/simplestart";

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label="Account"
        className={cn(
          "flex items-center gap-1 rounded-full pr-1 ring-2 transition-shadow",
          open ? "ring-primary" : "ring-transparent hover:ring-white-200"
        )}
      >
        <Avatar src={avatarSrc} alt="Account" size="sm" />
        <ChevronDown className={cn("size-3.5 text-grey-300 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div
          role="menu"
          className="animate-slip-card-enter absolute right-0 top-full z-[60] mt-2 max-h-[calc(100dvh-96px)] w-[320px] overflow-y-auto rounded-[20px] bg-grey-900 shadow-xl ring-1 ring-white/15"
        >
          {/* The creator. Opens Creator home. */}
          <Link
            href={planHref("/creator/home")}
            role="menuitem"
            className="group flex items-center gap-3.5 border-b border-white-100 px-5 py-4 transition-colors hover:bg-white-100"
          >
            <Avatar src={avatarSrc} alt="" size="md" className="ring-1 ring-white/15" />
            <span className="min-w-0 flex-1">
              <span className="block truncate font-open-sauce text-[17px] font-black tabular-nums tracking-tight text-white">
                {wallet ? shortWallet(wallet) : "Creator"}
              </span>
              <span className="mt-0.5 block font-open-sauce text-[13px] font-semibold tracking-tight text-grey-300 transition-colors group-hover:text-grey-100">
                Creator home
              </span>
            </span>
            <ChevronRight className="size-5 shrink-0 text-grey-300 transition-colors group-hover:text-white" />
          </Link>

          <div className="py-1.5">
            {rows.map((item) =>
              inSimple ? (
                <button
                  key={item.href}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    window.dispatchEvent(new CustomEvent(SIMPLE_VIEW_EVENT, { detail: item.view }));
                  }}
                  className={row}
                >
                  <item.Icon className={glyph} />
                  {item.label}
                  {item.fact && <span className={fact}>{item.fact}</span>}
                </button>
              ) : (
                <Link key={item.href} href={item.href} role="menuitem" className={row}>
                  <item.Icon className={glyph} />
                  {item.label}
                  {item.fact && <span className={fact}>{item.fact}</span>}
                </Link>
              )
            )}
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                setHubOpen(true);
              }}
              className={row}
            >
              <Gift className={glyph} />
              Reward hub
              {hub.claimableCount > 0 && (
                <span className={cn(fact, "text-creator")}>{hub.claimableCount} ready</span>
              )}
            </button>
            <Link href={planHref("/creator/home?tab=affiliate")} role="menuitem" className={row}>
              <UserPlus className={glyph} />
              Invite &amp; earn
              <span className={cn(fact, "text-creator")}>${commission.toFixed(2)}</span>
            </Link>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                // /line, /line+ and /simple have their own guide; everywhere else, the onboarding.
                if (pathname === "/line" || pathname === "/line+" || pathname === "/simple" || pathname === "/simplestart") window.dispatchEvent(new CustomEvent(LINE_HOWTO_EVENT));
                else openOnboarding();
              }}
              className={row}
            >
              <BookOpen className={glyph} />
              How to play
            </button>
          </div>

          <div className="border-t border-white-100 py-1.5">
            <button
              type="button"
              role="menuitem"
              aria-expanded={helpOpen}
              onClick={() => setHelpOpen(!helpOpen)}
              className={row}
            >
              <CircleHelp className={glyph} />
              Help & rules
              <ChevronDown
                className={cn(
                  "ml-auto size-4 shrink-0 text-grey-300 transition-transform duration-300",
                  helpOpen && "rotate-180"
                )}
              />
            </button>
            {helpOpen && (
              <div className="pb-1">
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setOpen(false);
                    setRulesOpen(true);
                  }}
                  className={sub}
                >
                  Game rules
                </button>
                {HELP_LINKS.map((link) =>
                  link.external ? (
                    <a key={link.label} href={link.href} target="_blank" rel="noreferrer" role="menuitem" className={sub}>
                      {link.label}
                    </a>
                  ) : (
                    <Link key={link.label} href={link.href} role="menuitem" className={sub}>
                      {link.label}
                    </Link>
                  )
                )}
                <div className="flex items-center gap-1 py-2 pl-[46px] pr-5">
                  {SOCIAL_LINKS.map(({ label, href, Icon }) => (
                    <a
                      key={label}
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={label}
                      className="flex size-8 items-center justify-center rounded-lg text-grey-300 transition-colors hover:bg-white-100 hover:text-white"
                    >
                      <Icon className="size-4" />
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-white-100 py-1.5">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
              className={cn(row, "text-grey-300")}
            >
              <LogOut className={glyph} />
              Log out
            </button>
          </div>
        </div>
      )}

      <HowArenaV2WorksDialog open={rulesOpen} onOpenChange={setRulesOpen} />

      <Dialog open={hubOpen} onOpenChange={setHubOpen}>
        <DialogContent className="w-auto max-w-[calc(100vw-24px)] gap-0 overflow-hidden rounded-[20px] border-0 bg-grey-900 p-0 ring-1 ring-white/15 [&>button]:hidden">
          <DialogTitle className="sr-only">Reward Hub</DialogTitle>
          <RewardsHubBody data={hub} onClose={() => setHubOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
